import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, ArrowRight, Users, Heart, Brain, Briefcase, DollarSign, Activity } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const warmUpBackend = async () => {
      try {
        await Promise.allSettled([
          api.get('/health'),
          api.get('/assessments')
        ]);
      } catch (error) {
        // Intentionally silent
      }
    };

    warmUpBackend();
  }, []);

  const elements = [
    { icon: Briefcase, labelKey: 'workCareer', color: 'bg-blue-100 text-blue-700', descKey: 'workCareerDescFull' },
    { icon: Heart, labelKey: 'lovedOnes', color: 'bg-pink-100 text-pink-700', descKey: 'lovedOnesDescFull' },
    { icon: Brain, labelKey: 'emotionalMental', color: 'bg-purple-100 text-purple-700', descKey: 'emotionalMentalDescFull' },
    { icon: Users, labelKey: 'interactionsOthers', color: 'bg-green-100 text-green-700', descKey: 'interactionsOthersDescFull' },
    { icon: Activity, labelKey: 'physical', color: 'bg-orange-100 text-orange-700', descKey: 'physicalDescFull' },
    { icon: DollarSign, labelKey: 'financial', color: 'bg-brand-100 text-brand-700', descKey: 'financialDescFull' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-brand-50/30 to-white">
      {/* Header */}
      <header className="border-b border-brand-100 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/spc-logo.png" alt="SPC Logo" className="w-12 h-12 object-contain rounded-lg" />
              <div>
                <h1 className="text-xl font-black text-gray-800" style={{ fontFamily: 'Avenir, sans-serif' }}>
                  {t('appTitle')}
                </h1>
                <p className="text-xs text-gray-600">{t('subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                    language === 'en'
                      ? 'bg-brand-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="lang-en-btn"
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-3 py-1.5 text-sm font-semibold transition-colors ${
                    language === 'fr'
                      ? 'bg-brand-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="lang-fr-btn"
                >
                  FR
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                className="border-brand-600 text-brand-600 hover:bg-brand-50"
              >
                {t('signIn')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-block px-4 py-2 bg-brand-100 rounded-full">
                <span className="text-brand-700 font-semibold text-sm">{t('tagline')}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-tight">
                {t('heroTitle1')}
                <br />
                <span className="text-brand-600">{t('heroTitle2')}</span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
                {t('heroDescription')}
              </p>
            </div>

            <div className="space-y-4">
              <Button
                size="lg"
                onClick={() => navigate('/assessment/start')}
                className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                data-testid="begin-assessment-btn"
              >
                {t('beginAssessment')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle2 className="h-5 w-5 text-brand-600" />
                <span>{t('assessmentInfo')}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="text-3xl font-black text-brand-600">30</div>
                <div className="text-sm text-gray-600">Questions</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-brand-600">6</div>
                <div className="text-sm text-gray-600">{language === 'fr' ? 'Éléments' : 'Elements'}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-brand-600">5-7</div>
                <div className="text-sm text-gray-600">{language === 'fr' ? 'Minutes' : 'Minutes'}</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-6 border border-gray-100">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold text-gray-900">{t('elementsTitle')}</h3>
                <p className="text-gray-600">{t('elementsSubtitle')}</p>
              </div>

              <div className="space-y-4">
                {elements.map((element, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`p-3 rounded-lg ${element.color}`}>
                      <element.icon className="h-6 w-6" />
                    </div>
                    <span className="font-semibold text-gray-800">{t(element.labelKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6 Elements Detailed Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
              {t('elementsExplainedTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('elementsExplainedSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {elements.map((element, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${element.color}`}>
                    <element.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{t(element.labelKey)}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{t(element.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 bg-gradient-to-br from-brand-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-6">
                {language === 'fr' ? 'Ce que vous obtiendrez' : 'What You\'ll Get'}
              </h2>
              <ul className="space-y-4">
                {[
                  language === 'fr' ? 'Un aperçu clair de votre état de bien-être actuel' : 'A clear picture of your current well-being state',
                  language === 'fr' ? 'Identification des domaines nécessitant de l\'attention' : 'Identification of areas that need attention',
                  language === 'fr' ? 'Un rapport PDF personnalisé de 3 pages' : 'A personalized 3-page PDF report',
                  language === 'fr' ? 'Des recommandations d\'action concrètes' : 'Actionable recommendations',
                  language === 'fr' ? 'La possibilité de suivre vos progrès au fil du temps' : 'The ability to track your progress over time'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-brand-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl font-bold mb-6">{language === 'fr' ? 'Votre rapport inclut:' : 'Your Report Includes:'}</h3>
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="font-bold text-lg mb-1">{language === 'fr' ? 'État général du bien-être' : 'Overall Well-being State'}</div>
                  <div className="text-brand-100">{language === 'fr' ? 'Un aperçu de votre situation actuelle' : 'A snapshot of where you are today'}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="font-bold text-lg mb-1">{language === 'fr' ? 'Répartition par élément' : 'Element Breakdown'}</div>
                  <div className="text-brand-100">{language === 'fr' ? 'Représentation visuelle des 6 domaines' : 'Visual representation across all 6 areas'}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="font-bold text-lg mb-1">{language === 'fr' ? 'Recommandations d\'action' : 'Action Recommendations'}</div>
                  <div className="text-brand-100">{language === 'fr' ? 'Prochaines étapes personnalisées' : 'Personalized next steps to consider'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/spc-logo.png" alt="SPC Logo" className="w-10 h-10 object-contain rounded-lg" />
              <div>
                <div className="text-white font-bold">{t('appTitle')}</div>
                <div className="text-sm">{t('subtitle')}</div>
              </div>
            </div>
            <div className="text-sm text-center md:text-right">
              <p>© {language === 'fr' ? '2020-26 Centre des points forts. Tous droits réservés.' : '2020-26 Strengths Performance Centre. All rights reserved.'}</p>
              <p className="mt-1">
                {language === 'fr' ? 'bien-etre@spc-cpf.com | +1 (613) 421-4722 poste5' : 'well-being@spc-cpf.com | +1 (613) 421-4722 ext.5'}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
